/* =====================================================================
   oo-collection-interactions.js
   Shared behaviors for the OO collection page build.

   Provides:
     (1) Card click-to-PDP (anywhere not a button/form/link)
     (2) ATC form intercept (so card link doesn't hijack the click)
     (3) Notify-me trigger — CartBite modal if present, else PDP nav
     (4) Gold shimmer: fires once per element when it enters viewport
     (5) Entry animation controller (data-oo-animate)
     (6) Character stagger helper (for [data-oo-animate="stagger"])
     (7) Modal host: listens for oo:open-modal, opens matching modal section
     (8) Carousel arrow handlers (desktop only, progressively enhanced)
     (9) Expand-in-place toggle

   Single-file, vanilla, no dependencies. Guards on IntersectionObserver,
   respects prefers-reduced-motion.
   ===================================================================== */

(function () {
  'use strict';

  const prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----------------------------------------------------------------- */
  /* (1) Card click-to-PDP                                              */
  /* ----------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    const card = e.target.closest('.oo-card[data-product-url]');
    if (!card) return;
    // Don't navigate if the user clicked a real interactive element
    if (e.target.closest('a, button, form, input, select, [role="button"]')) return;
    const url = card.getAttribute('data-product-url');
    if (url) window.location.href = url;
  });

  /* ----------------------------------------------------------------- */
  /* (2) ATC form intercept — adds to cart without navigating,          */
  /*     then asks UpCart (or the theme cart drawer) to open.           */
  /* ----------------------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    const form = e.target.closest('form[data-oo-atc-form]');
    if (!form) return;
    e.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button) {
      button.disabled = true;
      button.dataset.origLabel = button.textContent;
      button.textContent = 'Adding…';
    }

    const formData = new FormData(form);
    fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function () {
        // Tell the theme cart drawer to open. Dispatches a few common events
        // so we cover whatever the theme / UpCart listens for.
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('oo:cart-added'));
        window.dispatchEvent(new CustomEvent('ajaxProduct:added'));
        // Fallback: open cart page after short delay if no drawer responds
        setTimeout(function () {
          if (button) {
            button.disabled = false;
            button.textContent = 'Added ✓';
            setTimeout(function () {
              button.textContent = button.dataset.origLabel || 'Add to cart';
            }, 1800);
          }
        }, 250);
      })
      .catch(function (err) {
        console.error('[oo] ATC failed', err);
        if (button) {
          button.disabled = false;
          button.textContent = 'Try again';
          setTimeout(function () {
            button.textContent = button.dataset.origLabel || 'Add to cart';
          }, 1600);
        }
      });
  });

  /* ----------------------------------------------------------------- */
  /* (3) Notify-me — integrate with CartBite (Back-in-Stock app)        */
  /*     CartBite exposes window.kbite if installed; fall back to a     */
  /*     custom event for future-proofing.                              */
  /* ----------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-oo-notify-me]');
    if (!btn) return;
    e.preventDefault();
    const productId = btn.getAttribute('data-product-id');
    const variantId = btn.getAttribute('data-variant-id');
    const handle = btn.getAttribute('data-product-handle');
    // CartBite / kbite native trigger (common selectors)
    if (window.kbite && typeof window.kbite.open === 'function') {
      window.kbite.open({ productId: productId, variantId: variantId });
      return;
    }
    // No kbite JS API exists on this store (verified 2026-08-02): CartBite only
    // self-injects its own button + modal on the PDP. Send the shopper there —
    // the PDP owns the working notify path (CartBite today; the Klaviyo OOS
    // flow after its go-live).
    if (handle) {
      window.location.href = '/products/' + handle;
      return;
    }
    // Last resort (button without a handle): dispatch for any listener.
    document.dispatchEvent(new CustomEvent('oo:notify-me', {
      detail: { productId: productId, variantId: variantId, handle: handle }
    }));
  });

  /* ----------------------------------------------------------------- */
  /* (4) + (5) IntersectionObserver: entry animations + gold shimmer    */
  /* ----------------------------------------------------------------- */
  if ('IntersectionObserver' in window && !prefersReducedMotion) {
    // Entry animation observer
    const animateObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        // Stagger handling first
        if (el.getAttribute('data-oo-animate') === 'stagger') {
          splitIntoChars(el);
          el.querySelectorAll('.oo-char').forEach(function (ch, i) {
            ch.style.transitionDelay = (i * 80) + 'ms';
          });
        }
        el.classList.add('oo-in-view');
        animateObserver.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });

    document.querySelectorAll('[data-oo-animate]').forEach(function (el) {
      animateObserver.observe(el);
    });

    // Gold shimmer observer
    const shimmerObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        // Delay 0.6s after visible, then trigger once
        setTimeout(function () {
          el.classList.add('oo-shimmer-run');
        }, 600);
        shimmerObserver.unobserve(el);
      });
    }, { threshold: 0.3 });

    document.querySelectorAll('[data-oo-gold-shimmer]').forEach(function (el) {
      shimmerObserver.observe(el);
    });
  } else {
    // No observer or reduced motion: reveal everything immediately
    document.querySelectorAll('[data-oo-animate]').forEach(function (el) {
      el.classList.add('oo-in-view');
    });
  }

  /* ----------------------------------------------------------------- */
  /* (6) Character splitter for stagger-fade headings                   */
  /* ----------------------------------------------------------------- */
  function splitIntoChars(el) {
    if (el.dataset.ooSplit === 'done') return;
    const raw = el.textContent;
    el.textContent = '';
    Array.from(raw).forEach(function (ch) {
      const span = document.createElement('span');
      span.className = 'oo-char';
      span.textContent = ch;
      el.appendChild(span);
    });
    el.dataset.ooSplit = 'done';
  }

  /* ----------------------------------------------------------------- */
  /* (7) Modal host: sections fire oo:open-modal; we open matching      */
  /*     .oo-modal[data-modal-id="X"] on the page.                      */
  /* ----------------------------------------------------------------- */
  document.addEventListener('oo:open-modal', function (e) {
    const modalId = e.detail && e.detail.modalId;
    if (!modalId) return;
    const modal = document.querySelector('.oo-modal[data-modal-id="' + modalId + '"]');
    if (!modal) {
      console.warn('[oo] Modal section not found on page: ' + modalId +
                   '. Add the matching modal section to this template.');
      return;
    }
    openModal(modal, e.detail.context || {});
  });

  function openModal(modal, context) {
    modal.hidden = false;
    modal.setAttribute('data-oo-modal-open', 'true');
    document.body.classList.add('oo-scroll-lock');
    // Pass context to the modal via dataset (for the modal's own JS to consume)
    modal.dataset.ooModalContext = JSON.stringify(context || {});
    // Dispatch an event the modal's own JS can listen for
    modal.dispatchEvent(new CustomEvent('oo:modal-opened', { detail: context }));
    // Escape key to close
    document.addEventListener('keydown', escapeModalHandler);
    // Click outside to close
    modal.addEventListener('click', outsideModalHandler);
    // Focus the first focusable child, or the close button
    requestAnimationFrame(function () {
      const focusable = modal.querySelector(
        '[data-oo-modal-close], button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable) focusable.focus();
    });
  }
  function closeModal(modal) {
    modal.hidden = true;
    modal.removeAttribute('data-oo-modal-open');
    document.body.classList.remove('oo-scroll-lock');
    modal.removeEventListener('click', outsideModalHandler);
    document.removeEventListener('keydown', escapeModalHandler);
    modal.dispatchEvent(new CustomEvent('oo:modal-closed'));
  }
  function escapeModalHandler(e) {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('.oo-modal[data-oo-modal-open="true"]');
    if (open) closeModal(open);
  }
  function outsideModalHandler(e) {
    if (e.target.matches('.oo-modal__backdrop')) {
      closeModal(e.currentTarget);
    }
    if (e.target.closest('[data-oo-modal-close]')) {
      closeModal(e.currentTarget);
    }
  }

  /* ----------------------------------------------------------------- */
  /* (8) Carousel arrows — progressively enhanced                        */
  /*     Data wiring on the carousel markup (built in the section):      */
  /*       .oo-carousel[data-oo-carousel]                                */
  /*         .oo-carousel__track (scroll container)                      */
  /*         .oo-carousel__prev, .oo-carousel__next (buttons)            */
  /* ----------------------------------------------------------------- */
  document.querySelectorAll('[data-oo-carousel]').forEach(function (carousel) {
    const track = carousel.querySelector('.oo-carousel__track');
    const prev = carousel.querySelector('.oo-carousel__prev');
    const next = carousel.querySelector('.oo-carousel__next');
    if (!track) return;

    function step() {
      const card = track.querySelector('.oo-card');
      if (!card) return 260;
      return card.offsetWidth + 20; // approximate scroll-step
    }
    function updateArrows() {
      if (!prev || !next) return;
      const max = track.scrollWidth - track.clientWidth;
      prev.toggleAttribute('disabled', track.scrollLeft <= 2);
      next.toggleAttribute('disabled', track.scrollLeft >= max - 2);
    }
    if (prev) prev.addEventListener('click', function () {
      track.scrollBy({ left: -step(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', function () {
      track.scrollBy({ left: step(), behavior: 'smooth' });
    });
    track.addEventListener('scroll', updateArrows, { passive: true });
    window.addEventListener('resize', updateArrows);
    updateArrows();
  });

  /* ----------------------------------------------------------------- */
  /* (9) Expand-in-place toggle                                          */
  /*                                                                     */
  /*  Contract (matches oo-themed-product-block.liquid section markup):  */
  /*    <section data-expand-state="collapsed">                          */
  /*      <div class="oo-themed__product oo-themed__product--hidden">…   */
  /*      <button data-oo-expand-toggle data-oo-target="#section-id">    */
  /*        <span data-oo-expand-label-collapsed>Show more ↓</span>      */
  /*        <span data-oo-expand-label-expanded hidden>Show less ↑</span>*/
  /*      </button>                                                      */
  /*    </section>                                                       */
  /*                                                                     */
  /*  CSS hides .oo-themed__product--hidden when                         */
  /*  [data-expand-state="collapsed"]; we just flip the state attr.      */
  /*                                                                     */
  /*  Rewritten in session 3 QA (P2): old contract used                  */
  /*  data-oo-expand-toggle as the target ID — that mismatch was the     */
  /*  reason "Show more" did nothing.                                    */
  /* ----------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    const toggle = e.target.closest('[data-oo-expand-toggle]');
    if (!toggle) return;

    // Target selector lives in data-oo-target (e.g. "#oo-themed-abc123").
    const selector = toggle.getAttribute('data-oo-target');
    const target = selector ? document.querySelector(selector) : null;
    if (!target) return;

    const isCollapsed = target.getAttribute('data-expand-state') === 'collapsed';
    target.setAttribute('data-expand-state', isCollapsed ? 'expanded' : 'collapsed');
    toggle.setAttribute('aria-expanded', isCollapsed ? 'true' : 'false');

    // Swap visibility of the two label spans inside the button.
    const collapsedLabel = toggle.querySelector('[data-oo-expand-label-collapsed]');
    const expandedLabel = toggle.querySelector('[data-oo-expand-label-expanded]');
    if (collapsedLabel) collapsedLabel.toggleAttribute('hidden', isCollapsed);
    if (expandedLabel)  expandedLabel.toggleAttribute('hidden', !isCollapsed);
  });

  /* ----------------------------------------------------------------- */
  /* (10) Editorial Trio (E1) — accordion expand/collapse  (v3)         */
  /*                                                                    */
  /*  Markup contract (oo-editorial-trio.liquid v3):                    */
  /*    <div class="oo-trio__cards" data-trio-cards                    */
  /*         data-trio-anim-dur="500">                                  */
  /*      <button class="oo-trio__card" data-trio-toggle               */
  /*              data-trio-card-id="1" data-trio-state="collapsed"     */
  /*              aria-expanded="false"                                 */
  /*              aria-controls="oo-trio-{section}-panel-1">            */
  /*        ...header content...                                        */
  /*      </button>                                                     */
  /*      <div class="oo-trio__panel" data-trio-panel-id="1"            */
  /*           aria-hidden="true">...body + cta...</div>                */
  /*      ...repeat for cards 2 and 3...                                */
  /*    </div>                                                          */
  /*                                                                    */
  /*  v3 — Behavior is BREAKPOINT-AWARE:                                */
  /*    Mobile  (<768px): multi-open. Each card toggles independently.  */
  /*                      Avoids the scroll-anchor surprise that        */
  /*                      happens when a closing panel above the next   */
  /*                      card pulls it up to the wrong viewport pos.   */
  /*    Desktop (>=768px): single-open with sequential close-then-open. */
  /*                      Old card collapses fully, then new one opens. */
  /*                      Avoids the "panel jumps from grid row 2 to    */
  /*                      row 4" jank when switching between cards.     */
  /*                                                                    */
  /*  CSS handles all visual transitions via attribute selectors        */
  /*  ([data-trio-state="expanded"] and [aria-hidden="false"]).         */
  /* ----------------------------------------------------------------- */
  function ooTrioSetState(container, cardId, expanded) {
    const card = container.querySelector(
      '[data-trio-toggle][data-trio-card-id="' + cardId + '"]'
    );
    const panel = container.querySelector('[data-trio-panel-id="' + cardId + '"]');
    if (card) {
      card.setAttribute('data-trio-state', expanded ? 'expanded' : 'collapsed');
      card.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
    if (panel) {
      panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    }
  }
  function ooTrioGetExpandedIds(container) {
    const ids = [];
    container.querySelectorAll('[data-trio-toggle]').forEach(function (c) {
      if (c.getAttribute('data-trio-state') === 'expanded') {
        ids.push(c.getAttribute('data-trio-card-id'));
      }
    });
    return ids;
  }
  function ooTrioReadAnimDur(container) {
    const raw = container.getAttribute('data-trio-anim-dur');
    const dur = raw ? parseInt(raw, 10) : NaN;
    return (!isNaN(dur) && dur > 0) ? dur : 500;
  }
  // Track which sections are mid-sequential-transition so we can
  // ignore clicks during the close-then-open handoff on desktop.
  const ooTrioSwitching = new WeakSet();

  document.addEventListener('click', function (e) {
    const toggle = e.target.closest('[data-trio-toggle]');
    if (!toggle) return;

    const container = toggle.closest('[data-trio-cards]');
    if (!container) return;
    if (ooTrioSwitching.has(container)) return;

    const cardId = toggle.getAttribute('data-trio-card-id');
    const isOpen = toggle.getAttribute('data-trio-state') === 'expanded';
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;

    // ----- MOBILE: multi-open. Just toggle this card. -----
    if (!isDesktop) {
      ooTrioSetState(container, cardId, !isOpen);
      return;
    }

    // ----- DESKTOP: single-open, sequential close-then-open. -----
    if (isOpen) {
      ooTrioSetState(container, cardId, false);
      return;
    }
    const openIds = ooTrioGetExpandedIds(container);
    if (openIds.length === 0) {
      ooTrioSetState(container, cardId, true);
      return;
    }
    // Close currently-open card(s), wait one transition, then open the new one.
    ooTrioSwitching.add(container);
    openIds.forEach(function (oid) { ooTrioSetState(container, oid, false); });
    setTimeout(function () {
      ooTrioSetState(container, cardId, true);
      ooTrioSwitching.delete(container);
    }, ooTrioReadAnimDur(container) + 40);
  });

  /* ----------------------------------------------------------------- */
  /* (11) Editorial Trio quick-view bridge  (v2)                        */
  /*                                                                    */
  /*  When CTA action is set to "quick_view" in the section, the button */
  /*  has class js-modal-open-quick-modal-{product_id} so Motion's      */
  /*  built-in modal handler binds it (Motion auto-binds clicks on      */
  /*  any class starting with js-modal-open-).                          */
  /*                                                                    */
  /*  Motion needs <div id="QuickShopModal-{product_id}"> on the page   */
  /*  for the modal to actually open. If that markup isn't present      */
  /*  (e.g., this collection page only has our custom B1/E1 sections    */
  /*  and no theme-rendered product cards), we fall back to PDP nav     */
  /*  so the user always reaches the product.                           */
  /*                                                                    */
  /*  We attach this listener AFTER Motion's would have bound — using   */
  /*  bubble phase + a check for the modal's existence.                 */
  /* ----------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    const trigger = e.target.closest('[data-oo-quick-view-handle]');
    if (!trigger) return;

    const handle = trigger.getAttribute('data-oo-quick-view-handle');
    const productId = trigger.getAttribute('data-oo-quick-view-product-id');
    const motionModal = productId
      ? document.getElementById('QuickShopModal-' + productId)
      : null;

    if (motionModal) {
      // The theme's QuickShopModal exists. Motion's own handler will
      // open it (binding via the js-modal-open-quick-modal-{id} class).
      // Don't preventDefault, don't reroute. Let Motion do its thing.
      return;
    }

    // Fallback: no modal on this page → navigate to the PDP.
    e.preventDefault();
    if (handle) {
      window.location.href = '/products/' + handle;
    }
  });

  /* ----------------------------------------------------------------- */
  /* (12) Editorial Panels (E2) — expanding panels handler  (v1)        */
  /*                                                                    */
  /*  Markup contract (oo-editorial-panels.liquid v1):                  */
  /*    <section id="oo-ep-{section.id}">                              */
  /*      <div class="oo-ep__panels" data-ep-panels                    */
  /*           data-ep-allow-collapse="true|false"                     */
  /*           data-ep-anim-dur="700">                                  */
  /*        <div class="oo-ep__panel" data-ep-panel                    */
  /*             data-ep-state="active|collapsed">                     */
  /*          ...image background, number badge, title, body...        */
  /*        </div>                                                     */
  /*        ...repeat for 3-6 panels...                                */
  /*      </div>                                                       */
  /*    </section>                                                     */
  /*                                                                    */
  /*  Behavior (driven by data attributes on the container):           */
  /*    - Single-open: clicking an inactive panel → it becomes active, */
  /*      all others go collapsed.                                     */
  /*    - allow-collapse=true: clicking the active panel collapses it  */
  /*      (all panels become collapsed — empty state allowed).         */
  /*    - allow-collapse=false: clicking the active panel does nothing */
  /*      (always at least one panel active).                          */
  /*                                                                    */
  /*  CSS handles all visual transitions via [data-ep-state] selector. */
  /*  Multiple sections on the same page are isolated (each container  */
  /*  manages its own panels independently).                           */
  /* ----------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    const panel = e.target.closest('[data-ep-panel]');
    if (!panel) return;

    const container = panel.closest('[data-ep-panels]');
    if (!container) return;

    const allowCollapse = container.getAttribute('data-ep-allow-collapse') === 'true';
    const wasActive = panel.getAttribute('data-ep-state') === 'active';

    // Step 1: collapse every panel in this container
    container.querySelectorAll('[data-ep-panel]').forEach(function (p) {
      p.setAttribute('data-ep-state', 'collapsed');
    });

    // Step 2: re-activate the clicked panel UNLESS it was already active
    // and collapse-on-tap is allowed.
    if (!wasActive || !allowCollapse) {
      panel.setAttribute('data-ep-state', 'active');
    }
  });

})();
